import { Mesh } from 'three/src/objects/Mesh';
import {
  dischargeFloorY, extractTriangles, FillAnchor, trianglesVolume
} from './stopeGeometry';

/**
 * Exact stope fill volumetry over a fill SURFACE, with no CSG anywhere.
 *
 * The fill surface is the pointwise MAXIMUM of every pour's cone of repose:
 *
 *     h(x, z) = max over pours of ( level_k - rho_k(x, z) * tan(theta_k) )
 *
 * which is what paste actually does - it piles on top of whatever is already there. Each pour adds its
 * own cone, anchored at its own discharge point, at its own beach angle; nothing already placed moves.
 *
 * THE INTEGRAL. Volume below the surface is evaluated per triangle by the divergence theorem with
 *
 *     F = (0, y - h(x, z), 0)      div F = 1
 *
 * F vanishes IDENTICALLY on the fill surface, where y = h, so the cap contributes nothing - for ANY h
 * whatsoever. That is the whole reason this works where the previous model could not. Each triangle
 * contributes its signed area projected on xz times the mean of (y - h) over the retained polygon, and no
 * cross-section is ever reconstructed.
 *
 * WHY NOT THE OLD SHEAR. The previous kernel sheared y by rho * tan(theta) to make the beach horizontal
 * and used F = (x, 0, 0). That is exact, but only for ONE cone: a max of cones from different anchors and
 * angles cannot be sheared flat, so it modelled the whole fill as a single cone carrying only a cumulative
 * volume forward. Changing a pour's angle then re-shaped the entire history - a 1 m pour's apex moved
 * 5.8 m on the angle alone - and once beaches stopped nesting the books stopped balancing, under-reporting
 * the filled volume by up to 30%. Measured against a CSG union of two cones at different anchors AND
 * angles, this integral agrees to 0.02-0.15%.
 *
 * TESSELLATION. h is interpolated linearly along each triangle edge but is curved, because rho is a
 * distance. refineForSurfaces splits until that error is bounded; see the note there. theta = 0 is a
 * horizontal plane, exact at any tessellation, and splits nothing.
 */

export interface BeachSurface {
  /** The cone's apex height. At the anchor rho is 0, so this is also its elevation there. */
  level: number;
  anchor: FillAnchor;
  slopeDegrees: number;
}

export interface FillCurve {
  /** The surfaces already placed. The curve varies only the NEW cone's level on top of these. */
  readonly previous: readonly BeachSurface[];
  readonly anchor: FillAnchor;
  readonly slopeDegrees: number;
  /** Volume under `previous` alone - what is in the stope before this pour. */
  readonly baseVolume: number;
  /** A level at or below which the new cone adds nothing. */
  readonly minLevel: number;
  /** The level at which the new cone alone fills the whole solid. */
  readonly maxLevel: number;
  /** Volume of the whole solid. */
  readonly totalVolume: number;
  /** TOTAL filled volume with the new cone at `level` - not the slice. Subtract baseVolume for that. */
  volumeAt(level: number): number;
  /** Monotone inverse of volumeAt, in TOTAL filled volume. */
  levelAt(volume: number): number;
  /** Height of the NEW cone alone at (x, z), ignoring what is underneath it. */
  surfaceYAt(level: number, x: number, z: number): number;
}

const DEFAULT_SAMPLES = 200;
const MIN_SAMPLES = 24;
// Triangle clips spent filling the bracketing table, traded against the refined triangle count. Only the
// BRACKET gets coarser - levelAt still finishes against the exact integral.
const SAMPLE_WORK_BUDGET = 3000000;
const SECANT_REFINEMENTS = 2;
// How far the fill surface may be misplaced within one triangle, in metres, before it is split.
const LEVEL_TOLERANCE = 0.05;
const MAX_REFINE_DEPTH = 7;
const MAX_REFINED_TRIANGLES = 400000;

const gradeOf = (slopeDegrees: number): number => Math.tan((slopeDegrees * Math.PI) / 180);

const dropAt = (anchor: FillAnchor, grade: number, x: number, z: number): number => (
  Math.hypot(x - anchor.x, z - anchor.z) * grade
);

/** Height of the fill surface at (x, z): the highest cone over that point, or -Infinity if none. */
export const surfaceHeightAt = (surfaces: readonly BeachSurface[], x: number, z: number): number => {
  let highest = -Infinity;
  surfaces.forEach((surface) => {
    const height = surface.level - dropAt(surface.anchor, gradeOf(surface.slopeDegrees), x, z);
    if (height > highest) highest = height;
  });
  return highest;
};

/**
 * Drops every cone that lies entirely under another, which cannot change the pointwise maximum.
 *
 * Cone A is inside cone B when `gradeA >= gradeB` and `levelA + d * gradeB <= levelB`, where d is the
 * distance between their anchors: by the triangle inequality B's height at any point is at least
 * `levelB - (rhoA + d) * gradeB`, so that condition makes A no higher than B everywhere.
 *
 * This is a large saving, not a tidy-up. Successive pours from an unmoved pipe at an unchanged angle are
 * concentric cones, and CSG unioning those is pathological - their lateral surfaces are near-coincident,
 * and each union feeds the next. Measured on a real stope at 1.25 degrees, the cutter union alone cost
 * 0 / 1 / 234 / 1914 / 13572 ms for pours 1 to 5. Pruning collapses that whole family to one cutter.
 */
export const pruneDominatedSurfaces = (surfaces: readonly BeachSurface[]): BeachSurface[] => {
  if (surfaces.length < 2) return [...surfaces];

  const isInside = (inner: BeachSurface, outer: BeachSurface): boolean => {
    const innerGrade = gradeOf(inner.slopeDegrees);
    const outerGrade = gradeOf(outer.slopeDegrees);
    if (innerGrade < outerGrade) return false;
    const gap = Math.hypot(inner.anchor.x - outer.anchor.x, inner.anchor.z - outer.anchor.z);
    return inner.level + gap * outerGrade <= outer.level;
  };

  // Index comparison breaks ties between exact duplicates, so one of them always survives.
  return surfaces.filter((candidate, i) => !surfaces.some((other, j) => (
    j !== i && isInside(candidate, other) && (j < i || !isInside(other, candidate))
  )));
};

/**
 * The elevation a pour discharging at `anchor` measures its height FROM: the fill surface directly under
 * its pipe, or the floor of the solid there when nothing has reached that far.
 *
 * This is what makes a height-defined pour mean what it says. The cone's apex is datum + height, and the
 * datum depends only on where the pipe is and what is already underneath it - never on the pour's own
 * beach angle - so changing the angle widens or narrows the cone without moving its peak.
 */
export const dischargeDatum = (
  mesh: Mesh,
  surfaces: readonly BeachSurface[],
  anchor: FillAnchor,
): number => Math.max(dischargeFloorY(mesh, anchor), surfaceHeightAt(surfaces, anchor.x, anchor.z));

/**
 * Splits triangles until h is linear across each to within LEVEL_TOLERANCE.
 *
 * Tested per CONE rather than on the max, and with the level dropped: the deviation of
 * `level - rho * grade` from its linear interpolation does not depend on level, so one refinement serves
 * every level the curve samples. Bounding each cone bounds their max.
 *
 * Splitting at edge MIDPOINTS keeps children coplanar with the parent, so the mesh and its volume are
 * untouched - only the resolution of the cut improves. Stope solids here are extraordinarily coarse (118
 * to 144 triangles for a whole stope), so at a steep beach this is not a rounding detail.
 */
const refineForSurfaces = (
  triangles: Float64Array,
  fields: ReadonlyArray<{ anchor: FillAnchor; grade: number }>,
): Float64Array => {
  const sloped = fields.filter((field) => field.grade > 0);
  if (sloped.length === 0 || triangles.length === 0) return triangles;

  const deviation = (t: number[], edge: number): number => {
    const i = edge * 3;
    const j = ((edge + 1) % 3) * 3;
    const midX = (t[i] + t[j]) / 2;
    const midZ = (t[i + 2] + t[j + 2]) / 2;

    let worst = 0;
    sloped.forEach(({ anchor, grade }) => {
      const linear = (dropAt(anchor, grade, t[i], t[i + 2]) + dropAt(anchor, grade, t[j], t[j + 2])) / 2;
      const actual = dropAt(anchor, grade, midX, midZ);
      const gap = Math.abs(actual - linear);
      if (gap > worst) worst = gap;
    });
    return worst;
  };

  const withinTolerance = (t: number[]): boolean => (
    deviation(t, 0) <= LEVEL_TOLERANCE && deviation(t, 1) <= LEVEL_TOLERANCE && deviation(t, 2) <= LEVEL_TOLERANCE
  );

  const out: number[] = [];
  const stack: Array<{ t: number[]; depth: number }> = [];

  for (let i = 0; i < triangles.length; i += 9) {
    stack.push({ t: Array.from(triangles.subarray(i, i + 9)), depth: 0 });

    while (stack.length > 0) {
      const { t, depth } = stack.pop()!;
      if (depth >= MAX_REFINE_DEPTH || out.length / 9 >= MAX_REFINED_TRIANGLES || withinTolerance(t)) {
        out.push(...t);
      } else {
        const mid = (a: number, b: number): number[] => [0, 1, 2].map((k) => (t[a + k] + t[b + k]) / 2);
        const ab = mid(0, 3);
        const bc = mid(3, 6);
        const ca = mid(6, 0);
        const a = t.slice(0, 3);
        const b = t.slice(3, 6);
        const c = t.slice(6, 9);
        [
          [...a, ...ab, ...ca], [...ab, ...b, ...bc], [...ca, ...bc, ...c], [...ab, ...bc, ...ca],
        ].forEach((child) => stack.push({ t: child, depth: depth + 1 }));
      }
    }
  }

  return Float64Array.from(out);
};

/**
 * Per-vertex data for one mesh + one set of previous surfaces + one new cone field, with everything that
 * does not depend on the new cone's LEVEL precomputed. Sampling a 200-point curve then costs one max and
 * one subtraction per vertex instead of re-evaluating every cone.
 */
interface PreparedFill {
  x: Float64Array;
  y: Float64Array;
  z: Float64Array;
  /** Fill height from the previous surfaces at this vertex; -Infinity where nothing reaches. */
  previousHeight: Float64Array;
  /** rho * grade for the NEW cone at this vertex, so its height is (level - this). */
  newDrop: Float64Array;
}

const prepare = (
  triangles: Float64Array,
  previous: readonly BeachSurface[],
  anchor: FillAnchor,
  grade: number,
): PreparedFill => {
  const count = triangles.length / 3;
  const x = new Float64Array(count);
  const y = new Float64Array(count);
  const z = new Float64Array(count);
  const previousHeight = new Float64Array(count);
  const newDrop = new Float64Array(count);

  for (let v = 0; v < count; v += 1) {
    const o = v * 3;
    x[v] = triangles[o];
    y[v] = triangles[o + 1];
    z[v] = triangles[o + 2];
    previousHeight[v] = surfaceHeightAt(previous, x[v], z[v]);
    newDrop[v] = dropAt(anchor, grade, x[v], z[v]);
  }

  return {
    x, y, z, previousHeight, newDrop,
  };
};

// Signed area projected on xz (the y component of the edge cross product) times the mean depth below the
// fill surface. This is F.n dS for F = (0, y - h, 0) over one planar triangle.
const depthContribution = (
  ax: number, aw: number, az: number,
  bx: number, bw: number, bz: number,
  cx: number, cw: number, cz: number,
): number => {
  const ux = bx - ax;
  const uz = bz - az;
  const vx = cx - ax;
  const vz = cz - az;
  return ((uz * vx - ux * vz) / 2) * ((aw + bw + cw) / 3);
};

/**
 * Volume of the solid below the fill surface, with the new cone placed at `level`. Pass -Infinity for
 * `level` to measure the previous surfaces alone.
 */
const volumeUnder = (prepared: PreparedFill, level: number): number => {
  const {
    x, y, z, previousHeight, newDrop,
  } = prepared;

  let total = 0;
  // A triangle cut by one surface yields at most 4 corners.
  const px = [0, 0, 0, 0];
  const pw = [0, 0, 0, 0];
  const pz = [0, 0, 0, 0];
  const cw = [0, 0, 0];

  for (let t = 0; t < x.length; t += 3) {
    for (let k = 0; k < 3; k += 1) {
      const v = t + k;
      const newHeight = level - newDrop[v];
      cw[k] = y[v] - (newHeight > previousHeight[v] ? newHeight : previousHeight[v]);
    }

    if (cw[0] > 0 && cw[1] > 0 && cw[2] > 0) {
      // Wholly above the fill: contributes nothing.
    } else if (cw[0] <= 0 && cw[1] <= 0 && cw[2] <= 0) {
      total += depthContribution(
        x[t], cw[0], z[t],
        x[t + 1], cw[1], z[t + 1],
        x[t + 2], cw[2], z[t + 2],
      );
    } else {
      let count = 0;
      for (let corner = 0; corner < 3; corner += 1) {
        const next = (corner + 1) % 3;
        const wp = cw[corner];
        const wq = cw[next];
        if (wp <= 0) {
          px[count] = x[t + corner]; pw[count] = wp; pz[count] = z[t + corner];
          count += 1;
        }
        if ((wp < 0 && wq > 0) || (wp > 0 && wq < 0)) {
          const s = -wp / (wq - wp);
          px[count] = x[t + corner] + (x[t + next] - x[t + corner]) * s;
          pw[count] = 0;
          pz[count] = z[t + corner] + (z[t + next] - z[t + corner]) * s;
          count += 1;
        }
      }

      for (let fan = 1; fan < count - 1; fan += 1) {
        total += depthContribution(
          px[0], pw[0], pz[0],
          px[fan], pw[fan], pz[fan],
          px[fan + 1], pw[fan + 1], pz[fan + 1],
        );
      }
    }
  }

  return Math.abs(total);
};

/** Volume of the solid under a set of beach surfaces. Used for the running total and for tests. */
export const volumeUnderSurfaces = (mesh: Mesh, surfaces: readonly BeachSurface[]): number => {
  if (surfaces.length === 0) return 0;

  const pruned = pruneDominatedSurfaces(surfaces);
  const fields = pruned.map((s) => ({ anchor: s.anchor, grade: gradeOf(s.slopeDegrees) }));
  const triangles = refineForSurfaces(extractTriangles(mesh.geometry), fields);
  const last = pruned[pruned.length - 1];
  const prepared = prepare(triangles, pruned.slice(0, -1), last.anchor, gradeOf(last.slopeDegrees));
  return volumeUnder(prepared, last.level);
};

const clamp = (value: number, low: number, high: number): number => Math.min(Math.max(value, low), high);

/**
 * The curve for ONE pour: total filled volume as a function of that pour's cone level, with everything
 * already placed held fixed. Monotone by construction - raising a cone can only add material - so the
 * inverse needs no search beyond a bracket and a couple of secant steps.
 */
export const buildFillCurve = (
  mesh: Mesh,
  placed: readonly BeachSurface[],
  anchor: FillAnchor,
  slopeDegrees: number,
  samples?: number,
): FillCurve => {
  const grade = gradeOf(slopeDegrees);
  // Pruning first cuts both the per-vertex max and the number of refinement constraints.
  const previous = pruneDominatedSurfaces(placed);
  const fields = [
    ...previous.map((s) => ({ anchor: s.anchor, grade: gradeOf(s.slopeDegrees) })),
    { anchor, grade },
  ];
  const source = extractTriangles(mesh.geometry);
  const triangles = refineForSurfaces(source, fields);
  const prepared = prepare(triangles, previous, anchor, grade);

  // rho * grade >= 0, so min(y) is always at or below the true bottom of the field, and it is exact at
  // theta = 0. Deriving it from min(level) over vertices instead would overstate it - the field is convex
  // on a triangle, so its minimum can sit in the interior of a coarse floor triangle.
  let minLevel = Infinity;
  let maxLevel = -Infinity;
  for (let v = 0; v < prepared.y.length; v += 1) {
    if (prepared.y[v] < minLevel) minLevel = prepared.y[v];
    const level = prepared.y[v] + prepared.newDrop[v];
    if (level > maxLevel) maxLevel = level;
  }
  if (!Number.isFinite(minLevel)) minLevel = 0;
  if (!Number.isFinite(maxLevel)) maxLevel = 0;

  const baseVolume = previous.length > 0 ? volumeUnder(prepared, -Infinity) : 0;
  const totalVolume = trianglesVolume(source);

  const triangleCount = Math.max(1, prepared.y.length / 3);
  const stepCount = Math.max(2, samples ?? clamp(
    Math.floor(SAMPLE_WORK_BUDGET / triangleCount), MIN_SAMPLES, DEFAULT_SAMPLES,
  ));

  const span = maxLevel - minLevel;
  const levels = new Float64Array(stepCount + 1);
  const volumes = new Float64Array(stepCount + 1);
  for (let i = 0; i <= stepCount; i += 1) {
    levels[i] = minLevel + (span * i) / stepCount;
    // Clamped to monotone: the integral is exact, but floating-point noise could leave the table dipping
    // by a fraction of a cubic millimetre, which would break the binary search below.
    const raw = volumeUnder(prepared, levels[i]);
    volumes[i] = i === 0 ? Math.max(raw, baseVolume) : Math.max(raw, volumes[i - 1]);
  }

  const volumeAt = (level: number): number => {
    if (level <= minLevel) return volumes[0];
    if (level >= maxLevel) return volumes[stepCount];
    return Math.max(baseVolume, volumeUnder(prepared, level));
  };

  const levelAt = (volume: number): number => {
    const target = clamp(volume, volumes[0], volumes[stepCount]);
    if (target <= volumes[0]) return minLevel;
    if (target >= volumes[stepCount]) return maxLevel;

    let low = 0;
    let high = stepCount;
    while (high - low > 1) {
      const mid = Math.floor((low + high) / 2);
      if (volumes[mid] <= target) low = mid; else high = mid;
    }

    let lowLevel = levels[low];
    let highLevel = levels[high];
    let lowVolume = volumes[low];
    let highVolume = volumes[high];
    const bracket = highVolume - lowVolume;
    let guess = bracket > 0
      ? lowLevel + ((target - lowVolume) / bracket) * (highLevel - lowLevel)
      : lowLevel;

    // The table is piecewise linear but V is not, so refine against the exact integral.
    for (let step = 0; step < SECANT_REFINEMENTS; step += 1) {
      const guessVolume = volumeUnder(prepared, guess);
      if (guessVolume <= target) {
        lowLevel = guess;
        lowVolume = guessVolume;
      } else {
        highLevel = guess;
        highVolume = guessVolume;
      }
      const width = highVolume - lowVolume;
      guess = width > 0
        ? lowLevel + ((target - lowVolume) / width) * (highLevel - lowLevel)
        : (lowLevel + highLevel) / 2;
    }

    return clamp(guess, minLevel, maxLevel);
  };

  const surfaceYAt = (level: number, x: number, z: number): number => level - dropAt(anchor, grade, x, z);

  return {
    previous,
    anchor,
    slopeDegrees,
    baseVolume,
    minLevel,
    maxLevel,
    totalVolume,
    volumeAt,
    levelAt,
    surfaceYAt,
  };
};

// Curves are memoised per geometry + previous surfaces + anchor + slope. Building one is the expensive
// part (refinement plus a 200-point table); the drawer rebuilds the "next slice" curve on every keystroke.
const CURVE_KEY_PRECISION = 3;
const curveCache = new WeakMap<object, Map<string, FillCurve>>();

const curveKey = (previous: readonly BeachSurface[], anchor: FillAnchor, slopeDegrees: number): string => {
  const fixed = (value: number): string => value.toFixed(CURVE_KEY_PRECISION);
  const stack = previous
    .map((s) => `${fixed(s.level)}@${fixed(s.anchor.x)},${fixed(s.anchor.z)}/${s.slopeDegrees}`)
    .join('|');
  return `${stack}>>${fixed(anchor.x)},${fixed(anchor.z)}/${slopeDegrees}`;
};

export const getFillCurve = (
  mesh: Mesh,
  previous: readonly BeachSurface[],
  anchor: FillAnchor,
  slopeDegrees: number,
): FillCurve => {
  let byKey = curveCache.get(mesh.geometry);
  if (!byKey) {
    byKey = new Map<string, FillCurve>();
    curveCache.set(mesh.geometry, byKey);
  }

  const key = curveKey(pruneDominatedSurfaces(previous), anchor, slopeDegrees);
  const cached = byKey.get(key);
  if (cached) return cached;

  const curve = buildFillCurve(mesh, previous, anchor, slopeDegrees);
  byKey.set(key, curve);
  return curve;
};
